import type { AbilityAdvancedConfiguration, AbilityTemplateKey } from './combat-advanced';
export type UserRole = 'admin' | 'editor';
export type ItemStatus = 'draft' | 'review' | 'approved' | 'archived';
export type ItemRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'unique';
export type DebugLogLevel = 'debug' | 'info' | 'warn' | 'error';
export type UnrealSyncState = 'pending' | 'synced' | 'update_required' | 'error' | 'delete_requested' | 'delete_error' | 'deleted';

export type CombatContentStatus = 'draft' | 'review' | 'approved' | 'archived';
export type CombatSyncState = 'pending' | 'synced' | 'update_required' | 'error';

export type GameplayCatalogType = 'domain' | 'attribute' | 'world_interaction';
export interface GameplayCatalogEntry {
  type: GameplayCatalogType;
  technicalKey: string;
  displayName: string;
  description: string;
  sortOrder: number;
  active: boolean;
  damageNumberColorHex: string;
}

export interface GameplayPreset {
  type: 'range' | 'radius';
  technicalKey: string;
  displayName: string;
  description: string;
  valueCm: number;
  sortOrder: number;
  active: boolean;
}

export type AbilityForm =
  | 'hitscan_target'
  | 'self'
  | 'projectile'
  | 'beam'
  | 'persistent_area'
  | 'aoe_target'
  | 'aoe_self'
  | 'passive';
export type AreaOrigin = 'target' | 'self';
export type CriticalMode = 'none' | 'possible' | 'always';
export type AbilityActivationMode = 'press' | 'hold_repeat' | 'channel';
export type GameplayOutcomeType =
  | 'damage'
  | 'damage_over_time'
  | 'healing'
  | 'healing_over_time'
  | 'buff'
  | 'debuff'
  | 'world_interaction';
export type GameplayValueCalculation = 'flat' | 'target_max_hp_percent' | 'instigator_max_hp_percent';
export type InteractionStrengthMode = 'primary' | 'fixed';

export interface GameplayOutcome {
  id?: string;
  type: GameplayOutcomeType;
  baseValue: number | null;
  valueCalculation: GameplayValueCalculation;
  durationSeconds: number | null;
  attributeKey: string | null;
  modifierPercent: number | null;
  interactionKey: string | null;
  strengthMode: InteractionStrengthMode;
  fixedStrength: number | null;
}

export interface CombatAbilityMetrics {
  estimatedDamage: number;
  estimatedHealing: number;
  estimatedDurationSeconds: number;
  estimatedDps: number;
  estimatedHps: number;
  criticalDamageMaximum: number;
  notes: string[];
}

export interface CombatKitBalance {
  theoreticalDps: number;
  theoreticalHps: number;
  lightDps: number;
  heavyDps: number;
  ultimateDps: number;
  lightHps: number;
  heavyHps: number;
  ultimateHps: number;
  notes: string[];
}

export interface CombatValidation {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export interface CombatUnrealSync {
  state: CombatSyncState;
  importedRevision: number;
  attemptedRevision: number;
  assetPath: string | null;
  engineVersion: string | null;
  pluginVersion: string | null;
  machineName: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  errorDetails: unknown | null;
  firstImportedAt: string | null;
  lastAttemptAt: string | null;
  lastSucceededAt: string | null;
}

export interface CombatAbilityOption {
  id: string;
  technicalKey: string;
  displayName: string;
  domainKey: string;
  form: AbilityForm;
  status: CombatContentStatus;
  revision: number;
  valid: boolean;
  isComboRoot: boolean;
  comboStage: number;
  comboLength: number;
}

export interface CombatAbilityReference extends CombatAbilityOption {}
export interface CombatKitAbilityReference extends CombatAbilityOption {}

export interface CombatAbilityData {
  templateKey: AbilityTemplateKey;
  advancedConfig: AbilityAdvancedConfiguration;
  icon: FileSummary | null;
  id: string;
  technicalKey: string;
  displayName: string;
  description: string;
  form: AbilityForm;
  domainKey: string;
  /** Generated from the selected domain family. Editors cannot override it per ability. */
  damageNumberColorHex: string;
  cooldown: number;
  maximumRange: number;
  rangePresetKey: string | null;
  customMaximumRange: number | null;
  resolvedRadius: number;
  radiusPresetKey: string | null;
  customRadius: number | null;
  areaOrigin: AreaOrigin | null;
  areaDurationSeconds: number | null;
  allowFriendlyFire: boolean;
  activationMode: AbilityActivationMode;
  holdRepeatIntervalSeconds: number | null;
  channelDurationSeconds: number | null;
  channelTickIntervalSeconds: number | null;
  allowMovementDuringChannel: boolean;
  castTimeSeconds: number;
  allowMovementDuringCast: boolean;
  rotateToTarget: boolean;
  criticalMode: CriticalMode;
  comboNextAbilityId: string | null;
  comboWindowSeconds: number | null;
  comboNextAbility: CombatAbilityReference | null;
  comboStage: number;
  comboLength: number;
  isComboRoot: boolean;
  outcomes: GameplayOutcome[];
  metrics: CombatAbilityMetrics;
  status: CombatContentStatus;
  revision: number;
  validation: CombatValidation;
  unrealSync: CombatUnrealSync;
  createdAt: string;
  updatedAt: string;
}

export type CombatAbilityInput = Omit<CombatAbilityData,
  'id'|'icon'|'damageNumberColorHex'|'comboNextAbility'|'comboStage'|'comboLength'|'isComboRoot'|'metrics'|'revision'|'validation'|'unrealSync'|'createdAt'|'updatedAt'>;

export interface CombatKitData {
  icon: FileSummary | null;
  id: string;
  technicalKey: string;
  displayName: string;
  description: string;
  lightAbilityId: string | null;
  heavyAbilityId: string | null;
  ultimateAbilityId: string | null;
  passiveAbilityIds: string[];
  lightAbility: CombatKitAbilityReference | null;
  heavyAbility: CombatKitAbilityReference | null;
  ultimateAbility: CombatKitAbilityReference | null;
  passiveAbilities: CombatKitAbilityReference[];
  balance: CombatKitBalance;
  status: CombatContentStatus;
  revision: number;
  validation: CombatValidation;
  unrealSync: CombatUnrealSync;
  createdAt: string;
  updatedAt: string;
}

export type CombatKitInput = Omit<CombatKitData,
  'id'|'icon'|'lightAbility'|'heavyAbility'|'ultimateAbility'|'passiveAbilities'|'balance'|'revision'|'validation'|'unrealSync'|'createdAt'|'updatedAt'>;

export interface ItemUseConfiguration {
  form: Exclude<AbilityForm, 'passive'>;
  domainKey: string;
  damageNumberColorHex?: string;
  maximumRange: number;
  rangePresetKey: string | null;
  customMaximumRange: number | null;
  resolvedRadius: number;
  radiusPresetKey: string | null;
  customRadius: number | null;
  areaOrigin: AreaOrigin | null;
  areaDurationSeconds: number | null;
  outcomes: GameplayOutcome[];
}

export interface CombatAbilityListEntry {
  icon: FileSummary | null;
  id: string;
  technicalKey: string;
  displayName: string;
  description: string;
  domainKey: string;
  form: AbilityForm;
  outcomeCount: number;
  status: CombatContentStatus;
  revision: number;
  valid: boolean;
  errorCount: number;
  comboStage: number;
  comboLength: number;
  unrealSyncState: CombatSyncState;
}

export interface CombatKitListEntry {
  icon: FileSummary | null;
  id: string;
  technicalKey: string;
  displayName: string;
  lightAbilityName: string | null;
  heavyAbilityName: string | null;
  ultimateAbilityName: string | null;
  status: CombatContentStatus;
  revision: number;
  valid: boolean;
  errorCount: number;
  unrealSyncState: CombatSyncState;
}

export interface CombatAbilityListResponse { abilities: CombatAbilityListEntry[]; }
export interface CombatKitListResponse { kits: CombatKitListEntry[]; }

export const ITEM_LIST_COLUMN_IDS = [
  'item',
  'technicalKey',
  'description',
  'rarity',
  'category',
  'subcategory',
  'contentStatus',
  'ingameStatus',
  'tags',
  'origins',
  'maximumStack',
  'buyPrice',
  'sellPrice',
  'revision',
  'iconStatus',
  'updatedAt',
  'createdAt',
  'ingameRevision',
  'ingameAssetPath',
  'ingameLastSucceededAt',
] as const;

export type ItemListColumn = typeof ITEM_LIST_COLUMN_IDS[number];

export const DEFAULT_ITEM_LIST_COLUMNS: ItemListColumn[] = [
  'item',
  'rarity',
  'category',
  'contentStatus',
  'ingameStatus',
  'maximumStack',
  'sellPrice',
  'updatedAt',
];

export const ITEM_FILTER_FIELDS = [
  'name',
  'description',
  'englishName',
  'englishDescription',
  'technicalKey',
  'status',
  'rarity',
  'category',
  'subcategory',
  'tag',
  'origin',
  'buyPrice',
  'sellPrice',
  'maximumStack',
  'revision',
  'hasIcon',
  'ingameState',
  'ingameRevision',
  'ingameErrorMessage',
  'ingameAssetPath',
  'createdAt',
  'updatedAt',
  'ingameLastSucceededAt',
] as const;

export type ItemFilterField = typeof ITEM_FILTER_FIELDS[number];

export const ITEM_FILTER_OPERATORS = [
  'contains',
  'not_contains',
  'equals',
  'not_equals',
  'starts_with',
  'ends_with',
  'greater_than',
  'greater_or_equal',
  'less_than',
  'less_or_equal',
  'before',
  'after',
  'on',
  'is_empty',
  'is_not_empty',
  'is_set',
  'is_not_set',
] as const;

export type ItemFilterOperator = typeof ITEM_FILTER_OPERATORS[number];

export interface ItemFilterRule {
  field: ItemFilterField;
  operator: ItemFilterOperator;
  value?: string | number;
}

export interface ItemFilterSet {
  match: 'all' | 'any';
  rules: ItemFilterRule[];
}

export interface UserSummary {
  id: string;
  username: string;
  displayName: string;
  role: UserRole;
}

export interface TranslationData {
  name: string;
  description: string;
}

export interface FileSummary {
  id: string;
  filename: string;
  contentType: string;
  fileSize: number;
  width: number;
  height: number;
  downloadUrl: string;
  thumbnailUrl?: string | null;
  thumbnailFileSize?: number | null;
}

export interface ItemUnrealSync {
  state: UnrealSyncState;
  importedRevision: number;
  attemptedRevision: number;
  assetPath: string | null;
  engineVersion: string | null;
  pluginVersion: string | null;
  machineName: string | null;
  lastAction: 'create' | 'update' | 'delete' | null;
  errorCode: string | null;
  errorMessage: string | null;
  errorDetails: unknown | null;
  firstImportedAt: string | null;
  lastAttemptAt: string | null;
  lastSucceededAt: string | null;
  deletionRequestedAt: string | null;
  ingameDeletedAt: string | null;
}

export interface ItemData {
  id: string;
  technicalKey: string;
  status: ItemStatus;
  rarity: ItemRarity;
  category: string;
  subcategory: string;
  origins: string[];
  tags: string[];
  buyPrice: number;
  sellPrice: number;
  maximumStack: number;
  useConfiguration: ItemUseConfiguration | null;
  translations: Record<'de' | 'en', TranslationData>;
  icon: FileSummary | null;
  revision: number;
  unrealSync: ItemUnrealSync;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface ItemListEntry {
  id: string;
  technicalKey: string;
  status: ItemStatus;
  rarity: ItemRarity;
  category: string;
  subcategory: string;
  origins: string[];
  tags: string[];
  buyPrice: number;
  sellPrice: number;
  maximumStack: number;
  name: string;
  description: string;
  icon: FileSummary | null;
  revision: number;
  unrealSync: ItemUnrealSync;
  createdAt: string;
  updatedAt: string;
}

export interface CategoryData { key: string; label: string; subcategories: { key: string; label: string }[]; }
export interface RarityData { key: ItemRarity; label: string; color: string; sortOrder: number; }
export interface TagGroupData { key: string; label: string; tags: { key: string; label: string }[]; }
export interface ReferenceData {
  categories: CategoryData[];
  origins: { key: string; label: string }[];
  rarities: RarityData[];
  tagGroups: TagGroupData[];
  domains: { key: string; label: string; description: string; damageNumberColorHex: string }[];
  attributes: { key: string; label: string; description: string }[];
  worldInteractions: { key: string; label: string; description: string }[];
  rangePresets: GameplayPreset[];
  radiusPresets: GameplayPreset[];
  statuses: { key: ItemStatus; label: string }[];
  limits: { name: number; description: number; imageBytes: number };
}

export interface ItemListStats {
  loaded: number;
  approved: number;
  review: number;
  errors: number;
  updateRequired: number;
  pending: number;
  synced: number;
  deletionPending: number;
  deletionErrors: number;
  ingameDeleted: number;
}

export interface ItemListResponse {
  items: ItemListEntry[];
  total: number;
  limit: number;
  offset: number;
  stats: ItemListStats;
}

export interface ItemListPreference {
  pageSize: 10 | 25 | 50 | 100;
  visibleColumns: ItemListColumn[];
}

export type ItemBulkOperation = 'add_tag' | 'remove_tag' | 'add_origin' | 'remove_origin' | 'set_status' | 'set_rarity';
export interface ItemBulkRequest { itemIds: string[]; operation: ItemBulkOperation; value: string; }
export interface ItemBulkResult { changedCount: number; unchangedCount: number; itemIds: string[]; }

export interface FieldErrorDetail { field: string; message: string; code?: string; }
export interface ApiErrorDetails { summary?: string; fieldErrors?: FieldErrorDetail[]; issues?: unknown; }
export interface ApiError { error: { code: string; message: string; details?: ApiErrorDetails | unknown; requestId?: string | null }; }
export interface SyncChange { cursor: number; action: 'create' | 'update' | 'archive' | 'restore' | 'icon' | 'bulk_update' | 'delete_requested' | 'delete_confirmed'; item: ItemData | null; itemId: string; }
export interface SyncResponse { changes: SyncChange[]; nextCursor: number; hasMore: boolean; }

export interface IngameDeletionRequest {
  itemId: string;
  technicalKey: string;
  name: string;
  assetPath: string;
  requestedRevision: number;
  state: 'requested' | 'error';
  errorCode: string | null;
  errorMessage: string | null;
  requestedAt: string;
  lastAttemptAt: string | null;
}

export interface DebugLogEntry {
  id: number;
  createdAt: string;
  level: DebugLogLevel;
  source: string;
  event: string;
  message: string;
  details: unknown | null;
  requestId: string | null;
  actorId: string | null;
  actorName: string | null;
  route: string | null;
  method: string | null;
  statusCode: number | null;
  durationMs: number | null;
  estimatedBytes: number;
}

export interface DebugLogListResponse {
  logs: DebugLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface DebugLogStats {
  count: number;
  totalBytes: number;
  oldestAt: string | null;
  newestAt: string | null;
  levels: Record<DebugLogLevel, number>;
}

export interface ItemBatchPreviewRow {
  rowNumber: number;
  name: string;
  technicalKey: string;
  valid: boolean;
  errors: string[];
}

export interface ItemBatchPreview {
  rows: ItemBatchPreviewRow[];
  totalCount: number;
  validCount: number;
  invalidCount: number;
  canImport: boolean;
  maxRows: number;
}

export interface ItemBatchImportResult {
  createdCount: number;
  items: { id: string; technicalKey: string; name: string }[];
}
